import { useMutation, useQueryClient } from 'react-query'
import { message } from 'antd'
import { postJSON } from 'common/services/api'
import { CreateDeviceRecordInput, DeviceRecord } from '../types'
import { UploadFile } from 'antd'
import { uploadDocuments } from '../../../../../common/utils/documentUpload'
import { DEVICE_RECORDS_CATEGORY_ID } from '../constants'
import useMixpanelEvent, {
  MixpanelEvents
} from '../../../../../hooks/useMixpanelEvent'

interface UseDeviceRecordCreateReturn {
  createDeviceRecord: (
    input: CreateDeviceRecordInput,
    residentId: string,
    fileList?: UploadFile[]
  ) => Promise<DeviceRecord>
  isCreating: boolean
}

export const useDeviceRecordCreate = (
  onSuccess?: () => void
): UseDeviceRecordCreateReturn => {
  const queryClient = useQueryClient()
  const sendMixpanelEvent = useMixpanelEvent()

  const mutation = useMutation(
    async ({
      input,
      residentId,
      fileList
    }: {
      input: CreateDeviceRecordInput
      residentId: string
      fileList?: UploadFile[]
    }) => {
      const hasFiles = fileList && fileList.length > 0
      let documentIds: string[] = []

      if (hasFiles) {
        try {
          documentIds = await uploadDocuments(
            fileList,
            residentId,
            DEVICE_RECORDS_CATEGORY_ID
          )
        } catch {
          throw new Error('Failed to upload documents')
        }
      }

      const response = await postJSON({
        url: `/v6/residents/${residentId}/device-records`,
        json: {
          ...input,
          ...(documentIds.length > 0 && { documents: documentIds })
        }
      })

      return response.data as DeviceRecord
    },
    {
      onSuccess: (data, variables) => {
        sendMixpanelEvent(MixpanelEvents.DEVICE_RECORD_ADDED, {
          residentId: variables.residentId,
          deviceRecordId: data.id
        })
        message.success('Device record created successfully')
        queryClient.invalidateQueries(['device-records', variables.residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to create device record')
      }
    }
  )

  const createDeviceRecord = async (
    input: CreateDeviceRecordInput,
    residentId: string,
    fileList?: UploadFile[]
  ): Promise<DeviceRecord> => {
    return mutation.mutateAsync({ input, residentId, fileList })
  }

  return {
    createDeviceRecord,
    isCreating: mutation.isLoading
  }
}
